// Typed SDK for business-gateway (packages/business-gateway). One function per route in that
// package's src/index.js route table — this file *is* the frontend's contract with that service;
// keep it in sync with the route table when either side changes; there's no OpenAPI spec.
import { apiRequest } from './client.js';
import type { Team, TeamMember } from '@/features/settings/useTeamMembers.js';

// ── Auth ─────────────────────────────────────────────────────────────────────────────────────
export const authApi = {
  /** Best-effort — callers should swallow failures, since local logout must proceed regardless. */
  logout(): Promise<void> {
    return apiRequest('gateway', 'POST', '/auth/logout');
  },
  requestCode(email: string, tenantId: string): Promise<void> {
    return apiRequest('gateway', 'POST', '/auth/request-code', {
      body: { email, tenant_id: tenantId },
    });
  },
  verifyCode(
    email: string,
    code: string,
    tenantId: string,
  ): Promise<{ session_token: string; tenant_id: string }> {
    return apiRequest('gateway', 'POST', '/auth/verify-code', {
      body: { code, email, tenant_id: tenantId },
    });
  },
};

// ── Subscription / billing ──────────────────────────────────────────────────────────────────
export interface TenantStatus {
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  invoiceLimit: number;
  invoiceUsed: number;
  seatCount: number | null;
  status: string;
  tier: string;
}

export const subscriptionApi = {
  createBillingPortalSession(body: {
    return_url: string;
    tenant_id: string;
  }): Promise<{ url: string }> {
    return apiRequest('gateway', 'POST', '/billing/portal', { auth: true, body });
  },
  createCheckoutSession(body: {
    cancel_url: string;
    seats?: number;
    success_url: string;
    tenant_id: string;
    tier: string;
  }): Promise<{ url: string }> {
    return apiRequest('gateway', 'POST', '/checkout/session', { auth: true, body });
  },
  status(tenantId: string): Promise<TenantStatus> {
    return apiRequest('gateway', 'GET', '/api/tenant/status', { query: { tenant_id: tenantId } });
  },
};

export const accountApi = {
  /** Throws ApiError with status 404 if the tenant was never seen server-side (normal for a
   * free-tier user who never checked out) — callers treat that as success, not failure. */
  deleteAccount(tenantId: string): Promise<void> {
    return apiRequest('gateway', 'DELETE', '/api/tenant', { body: { tenant_id: tenantId } });
  },
  getDigestOptIn(tenantId: string): Promise<{ opted_in: boolean }> {
    return apiRequest('gateway', 'GET', '/account/digest-opt-in', {
      auth: true,
      query: { tenant_id: tenantId },
    });
  },
  setDigestOptIn(tenantId: string, optedIn: boolean): Promise<void> {
    return apiRequest('gateway', 'POST', '/account/digest-opt-in', {
      auth: true,
      body: { opted_in: optedIn, tenant_id: tenantId },
    });
  },
};

// ── Invoicing ────────────────────────────────────────────────────────────────────────────────
export const invoicingApi = {
  createHostedInvoice(body: {
    amount_due: number;
    client_address: string;
    client_id: string;
    client_name: string;
    from_address: string | null;
    from_name: string | null;
    invoice_number: string;
    line_items: unknown;
    tax_rate: number;
    tenant_id: string;
  }): Promise<{ invoiceId: string }> {
    return apiRequest('gateway', 'POST', '/invoice', { body });
  },
  fetchPublicInvoice(invoiceId: string): Promise<unknown> {
    return apiRequest('gateway', 'GET', `/invoice/${invoiceId}`);
  },
  // No OpenAPI spec backs these routes (see file header) -- unknown[] rather than a fabricated
  // or circularly-imported type; each caller (InvoiceHistory.vue, useClientInvoices.ts) already
  // defines its own local interface for the shape it actually expects.
  listBillingHistory(tenantId: string): Promise<{ invoices: unknown[] }> {
    return apiRequest('gateway', 'GET', '/billing/invoices', { query: { tenant_id: tenantId } });
  },
  listClientInvoices(tenantId: string): Promise<{ invoices: unknown[] }> {
    return apiRequest('gateway', 'GET', '/client-invoices', { query: { tenant_id: tenantId } });
  },
  /** Fire-and-forget view-tracking ping — callers should swallow failures. */
  pingInvoiceView(invoiceId: string): Promise<void> {
    return apiRequest('gateway', 'GET', '/api/tracker/ping', { query: { invoice: invoiceId } });
  },
};

// ── Team ─────────────────────────────────────────────────────────────────────────────────────
export interface TeamAssignment {
  break_minutes: number;
  ends_at: number;
  id: string;
  is_locked: number;
  jws_signature: string | null;
  member_email: string;
  shift_date: string;
  starts_at: number;
  version: number;
  workplace_label: string;
}

export const teamApi = {
  createTeam(tenantId: string, name: string): Promise<void> {
    return apiRequest('gateway', 'POST', '/team/teams', {
      auth: true,
      body: { name, tenant_id: tenantId },
    });
  },
  inviteMember(body: {
    email: string;
    employee_id?: string;
    name: string;
    role: string;
    team_id: string;
    tenant_id: string;
  }): Promise<void> {
    return apiRequest('gateway', 'POST', '/team/members', { auth: true, body });
  },
  listAudit(tenantId: string): Promise<{ entries: unknown[] }> {
    return apiRequest('gateway', 'GET', '/team/audit', {
      auth: true,
      query: { tenant_id: tenantId },
    });
  },
  listMembers(tenantId: string, teamId?: string): Promise<{ members: TeamMember[] }> {
    return apiRequest('gateway', 'GET', '/team/members', {
      auth: true,
      query: { team_id: teamId, tenant_id: tenantId },
    });
  },
  listTeams(tenantId: string): Promise<{ teams: Team[] }> {
    return apiRequest('gateway', 'GET', '/team/teams', {
      auth: true,
      query: { tenant_id: tenantId },
    });
  },
  pullAssignments(tenantId: string): Promise<{ assignments: TeamAssignment[] }> {
    return apiRequest('gateway', 'GET', '/team/schedule-assignments', {
      auth: true,
      query: { tenant_id: tenantId },
    });
  },
  pushAssignment(body: {
    break_minutes: number;
    ends_at: number;
    id?: string;
    jws: string;
    member_email: string;
    shift_date: string;
    starts_at: number;
    tenant_id: string;
    workplace_label: string;
  }): Promise<{ id: string; version: number }> {
    return apiRequest('gateway', 'POST', '/team/schedule-assignments', { auth: true, body });
  },
  /** DELETE with a JSON body is nonstandard but matches what TeamGate.js expects server-side. */
  removeMember(memberId: string, tenantId: string): Promise<void> {
    return apiRequest('gateway', 'DELETE', `/team/members/${memberId}`, {
      auth: true,
      body: { tenant_id: tenantId },
    });
  },
  toggleLock(assignmentId: string, tenantId: string, locked: boolean): Promise<void> {
    return apiRequest('gateway', 'POST', `/team/schedule-assignments/${assignmentId}/lock`, {
      auth: true,
      body: { locked, tenant_id: tenantId },
    });
  },
  updateMemberRole(memberId: string, tenantId: string, role: string): Promise<void> {
    return apiRequest('gateway', 'PATCH', `/team/members/${memberId}`, {
      auth: true,
      body: { role, tenant_id: tenantId },
    });
  },
};

// ── Webhooks / integrations ─────────────────────────────────────────────────────────────────
export const webhookApi = {
  clearConfig(tenantId: string): Promise<void> {
    return apiRequest('gateway', 'DELETE', '/webhook-config', { body: { tenant_id: tenantId } });
  },
  dispatch(body: { event: string; payload: unknown; tenant_id: string }): Promise<void> {
    return apiRequest('gateway', 'POST', '/webhooks/dispatch', { body });
  },
  saveConfig(tenantId: string, webhookUrl: string, webhookSecret: string | null): Promise<void> {
    return apiRequest('gateway', 'POST', '/webhook-config', {
      body: { tenant_id: tenantId, webhook_secret: webhookSecret, webhook_url: webhookUrl },
    });
  },
};
